/**
 * Standard Questions Filter Modal
 * 
 * Modal for selecting branch, usecase, and subusecase for standard questions
 * 
 * @component
 */

import React, { useState, useMemo, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Dropdown } from "@voicecare.ai/ui-library";
import { useTheme } from "../../ThemeContext";
import { message } from "antd";

const StandardQuestionsFilterModal = ({
  open,
  onClose,
  onApply,
  customerDetails,
  initialValues,
  currentTab = 'standard'
}) => {
  const { themeMode, currentTheme } = useTheme();
  const [selectedBranch, setSelectedBranch] = useState(initialValues?.branch || null);
  const [selectedUsecase, setSelectedUsecase] = useState(initialValues?.usecase || null);
  const [selectedSubusecase, setSelectedSubusecase] = useState(initialValues?.subusecase || null);

  const isDark = themeMode === 'dark';
  const isStandardTab = currentTab === 'standard';

  /**
   * Reset form when initial values change
   */
  useEffect(() => {
    if (open) {
      setSelectedBranch(initialValues?.branch || null);
      setSelectedUsecase(initialValues?.usecase || null);
      setSelectedSubusecase(initialValues?.subusecase || null);
    }
  }, [open, initialValues]);

  /**
   * Format name for display
   */
  const formatName = (name) => {
    if (!name) return '';
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  /**
   * Get branch options
   */
  const branchOptions = useMemo(() => {
    if (!customerDetails?.useCases) return [];
    const uniqueBranches = [...new Set(customerDetails.useCases.map(uc => uc.branch))];
    return uniqueBranches.map(branch => ({
      value: branch,
      label: formatName(branch)
    }));
  }, [customerDetails]);

  /**
   * Get usecase options based on selected branch
   */
  const usecaseOptions = useMemo(() => {
    if (!selectedBranch || !customerDetails?.useCases) return [];
    const usecases = customerDetails.useCases
      .filter(uc => uc.branch === selectedBranch)
      .map(uc => uc.usecase);
    const uniqueUsecases = [...new Set(usecases)];
    return uniqueUsecases.map(usecase => ({
      value: usecase,
      label: formatName(usecase)
    }));
  }, [customerDetails, selectedBranch]);

  /**
   * Get subusecase options based on selected branch and usecase
   */
  const subusecaseOptions = useMemo(() => {
    if (!selectedBranch || !selectedUsecase || !customerDetails?.useCases) return [];
    const useCaseMatch = customerDetails.useCases.find(
      uc => uc.branch === selectedBranch && uc.usecase === selectedUsecase
    );
    if (!useCaseMatch) return [];
    return useCaseMatch.subUsecases.map(subusecase => ({
      value: subusecase,
      label: formatName(subusecase)
    }));
  }, [customerDetails, selectedBranch, selectedUsecase]);

  /**
   * Handle dropdown changes
   */
  const handleBranchChange = (selected) => {
    setSelectedBranch(selected?.value || null);
    setSelectedUsecase(null);
    setSelectedSubusecase(null);
  };

  const handleUsecaseChange = (selected) => {
    setSelectedUsecase(selected?.value || null);
    setSelectedSubusecase(null);
  };

  const handleSubusecaseChange = (selected) => {
    setSelectedSubusecase(selected?.value || null);
  };

  /**
   * Handle apply button
   */
  const handleApply = () => {
    // For standard tab, all fields are required
    if (isStandardTab && (!selectedBranch || !selectedUsecase || !selectedSubusecase)) {
      message.error('Please select all fields');
      return;
    }

    onApply({
      branch: selectedBranch,
      usecase: selectedUsecase,
      subusecase: selectedSubusecase
    });
  };

  /**
   * Handle clear filter button
   */
  const handleClearFilter = () => {
    onApply({
      branch: null,
      usecase: null,
      subusecase: null
    });
  };

  /**
   * Handle close
   */
  const handleClose = () => {
    onClose();
  };

  if (!open) return null;

  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10000
  };

  const modalStyle = {
    backgroundColor: isDark ? '#04002b' : '#ffffff',
    color: currentTheme.text.primary,
    borderRadius: '16px',
    maxWidth: '50rem',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: isDark 
      ? '0 20px 60px rgba(0, 0, 0, 0.5)' 
      : '0 20px 60px rgba(0, 0, 0, 0.15)'
  };

  return (
    <>
      <div style={overlayStyle} onClick={handleClose}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <Box 
            sx={{ 
              padding: '1.5rem',
              borderBottom: `1px solid ${isDark ? '#302d4c' : '#e9e8ea'}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Box>
              <Typography 
                sx={{ 
                  fontSize: '1.25rem', 
                  fontWeight: 600,
                  color: currentTheme.text.primary,
                  marginBottom: '0.25rem'
                }}
              >
                {isStandardTab ? 'Select Standard Questions Path' : 'Filter Questions'}
              </Typography>
              <Typography 
                sx={{ 
                  fontSize: '0.875rem', 
                  color: currentTheme.text.secondary
                }}
              >
                {isStandardTab 
                  ? 'Choose the branch, usecase, and sub usecase to view standard questions' 
                  : 'Optionally filter questions by branch, usecase, and sub usecase'}
              </Typography>
            </Box>
            
            <IconButton 
              onClick={handleClose}
              sx={{ 
                color: currentTheme.text.primary,
                '&:hover': {
                  backgroundColor: isDark ? '#302d4c' : '#f0f0f0'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box 
            sx={{ 
              padding: '1.5rem'
            }}
          >
            {/* Branch Dropdown */}
            <Box sx={{ marginBottom: '1.5rem' }}>
              <Dropdown
                label={`Branch${isStandardTab ? ' *' : ''}`}
                placeholder="Select Branch"
                options={branchOptions}
                value={selectedBranch ? { value: selectedBranch, label: formatName(selectedBranch) } : null}
                onChange={handleBranchChange}
                themeMode={themeMode}
                isSearchable={true}
                width="100%"
              />
            </Box>

            {/* Usecase Dropdown */}
            <Box sx={{ marginBottom: '1.5rem' }}>
              <Dropdown
                label={`Usecase${isStandardTab ? ' *' : ''}`}
                placeholder="Select Usecase"
                options={usecaseOptions}
                value={selectedUsecase ? { value: selectedUsecase, label: formatName(selectedUsecase) } : null}
                onChange={handleUsecaseChange}
                themeMode={themeMode}
                disabled={!selectedBranch}
                isSearchable={true}
                width="100%"
              />
            </Box>

            {/* Sub Usecase Dropdown */}
            <Box sx={{ marginBottom: '1.5rem' }}>
              <Dropdown
                label={`Sub Usecase${isStandardTab ? ' *' : ''}`}
                placeholder="Select Sub Usecase"
                options={subusecaseOptions}
                value={selectedSubusecase ? { value: selectedSubusecase, label: formatName(selectedSubusecase) } : null}
                onChange={handleSubusecaseChange}
                themeMode={themeMode}
                disabled={!selectedUsecase}
                isSearchable={true}
                width="100%"
              />
            </Box>
          </Box>

          {/* Footer */}
          <Box 
            sx={{ 
              padding: '1.5rem',
              borderTop: `1px solid ${isDark ? '#302d4c' : '#e9e8ea'}`,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2
            }}
          >
            {/* Clear Filter button (only for non-standard tabs) */}
            {!isStandardTab && (
              <button
                onClick={handleClearFilter}
                style={{
                  padding: '0.625rem 1.5rem',
                  borderRadius: '8px',
                  border: `1px solid ${isDark ? '#302d4c' : '#e9e8ea'}`,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                  color: '#e63e2b',
                  transition: 'all 0.2s'
                }}
              >
                Clear Filter
              </button>
            )}

            <Box sx={{ display: 'flex', gap: 2, marginLeft: 'auto' }}>
              <button
                onClick={handleClose}
                style={{
                  padding: '0.625rem 1.5rem',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  backgroundColor: isDark ? '#1a1640' : '#f0f0f0',
                  color: currentTheme.text.primary,
                  transition: 'all 0.2s'
                }}
              >
                Cancel
              </button>

              <button
                onClick={handleApply}
                disabled={isStandardTab && (!selectedBranch || !selectedUsecase || !selectedSubusecase)}
                style={{
                  padding: '0.625rem 1.5rem',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: (isStandardTab && (!selectedBranch || !selectedUsecase || !selectedSubusecase)) ? 'not-allowed' : 'pointer',
                  backgroundColor: (isStandardTab && (!selectedBranch || !selectedUsecase || !selectedSubusecase)) 
                    ? (isDark ? '#302d4c' : '#e9e8ea')
                    : '#FF6B47',
                  color: (isStandardTab && (!selectedBranch || !selectedUsecase || !selectedSubusecase))
                    ? currentTheme.text.secondary
                    : '#ffffff',
                  transition: 'all 0.2s',
                  opacity: (isStandardTab && (!selectedBranch || !selectedUsecase || !selectedSubusecase)) ? 0.6 : 1
                }}
              >
                Apply
              </button>
            </Box>
          </Box>
        </div>
      </div>
    </>
  );
};

export default StandardQuestionsFilterModal;

